import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loadNamespace } from '../../i18n';
import { useQuery } from '@tanstack/react-query';
import { Menu, Globe, Bell, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { notificationsApi } from '../../api';
import NotificationsPanel from './NotificationsPanel';

export default function TopBar({ onMenuToggle }) {
  const { user } = useAuth();
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation('common');
  const { t: tNotif } = useTranslation('notification');

  // The bell aria-label lives in the lazy 'notification' namespace; load it on
  // mount so the label never renders as a raw key before the panel is opened.
  useEffect(() => { loadNamespace('notification'); }, []);

  const isRTL = language === 'ar';
  const [notifOpen, setNotifOpen] = useState(false);

  // QA #7 — bell badge reads the live unread count from the API
  // instead of the legacy hardcoded "3". Refetches every 60s so
  // dashboards see new alerts without a full reload.
  const { data: countData } = useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: async () => {
      const res = await notificationsApi.getCount();
      return res.data?.data ?? res.data ?? { count: 0 };
    },
    enabled: Boolean(user),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const unreadCount = Math.max(0, Number(countData?.count) || 0);

  const initials = user
    ? `${(user.first_name || user.username || '')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase() || 'U'
    : 'U';

  const fullName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username || ''
    : '';

  const email = user?.email || '';

  return (
    <header className="bg-white border-b border-gray-200 h-16 px-6 flex items-center shrink-0">
      {/* Mobile hamburger */}
      <button
        onClick={onMenuToggle}
        className="p-2 text-gray-500 hover:text-gray-900 rounded-lg lg:hidden me-3"
      >
        <Menu size={20} />
      </button>

      {/* Search bar — wide, white, prominent */}
      <div className="relative flex-1 max-w-2xl">
        <Search size={18} className="absolute start-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder={t('searchPlaceholder')}
          className="w-full bg-white border border-gray-300 rounded-xl ps-11 pe-4 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400"
        />
      </div>

      {/* Spacer pushes right items to the end */}
      <div className="flex-1" />

      {/* Right actions */}
      <div className="flex items-center gap-5">
        {/* Language toggle */}
        <button
          onClick={() => setLanguage(isRTL ? 'en' : 'ar')}
          className="flex items-center gap-2 text-gray-600 hover:text-emerald-700 transition-colors"
        >
          <Globe size={18} />
          <span className="text-sm font-medium">{isRTL ? 'English' : 'عربي'}</span>
        </button>

        {/* Notification bell — opens slide-over panel (QA #7) */}
        <button
          type="button"
          onClick={() => setNotifOpen(true)}
          className="relative text-gray-500 hover:text-gray-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded"
          aria-label={
            unreadCount > 0
              ? tNotif('panel.bellLabelWithCount', { count: unreadCount })
              : tNotif('panel.bellLabel')
          }
          aria-haspopup="dialog"
          aria-expanded={notifOpen}
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <span
              className="absolute -top-1.5 -end-1.5 min-w-[20px] h-5 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 shadow-sm"
              aria-hidden="true"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {/* User profile — links to self-service profile page (M-FE-5) */}
        <Link
          to="/profile"
          className="flex items-center gap-3 ps-5 border-s border-gray-200 hover:opacity-80 transition-opacity rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          aria-label={t('viewProfile')}
        >
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-sm shrink-0">
            {initials}
          </div>
          <div className="hidden md:flex flex-col leading-tight">
            <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">{fullName}</span>
            <span className="text-xs text-gray-400 whitespace-nowrap">{email}</span>
          </div>
        </Link>
      </div>
      <NotificationsPanel open={notifOpen} onClose={() => setNotifOpen(false)} />
    </header>
  );
}
